import { useDispatch } from 'react-redux';
import React from 'react';
import { sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';
import { gridTableAnalysisActions } from '@constants/actionTypes';
import { AddPromptModal } from '@containers/TableAnalysis/tableComponents/AddPromptRow';
import { Body5, Container } from '@ui';
import { IconButton } from '@ui/buttons/IconButton';
import { Edit } from '@assets/icons/svgs/Edit';
import { Star } from '@assets/icons/svgs/Star';
import { CitationIcon } from '@assets/icons/svgs/CitationIcon';
import { Expand } from '@assets/icons/svgs/Expand';
import { Collapse } from '@assets/icons/svgs/Collapse';
import { CloseButton } from '@ui/buttons/CloseButton';
import styled from 'styled-components';
import { useFavoritePrompt } from '@hooks/features/useFavoritePrompt';
import { CellAnswer } from './CellAnswer';
import { AgentCellAnswer } from './AgentCellAnswer';
import { DiffModeFlag } from '@containers/TableAnalysis/tableComponents/DiffModeFlag';

const TableRowWrapper = styled.tr``;
const CellActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;

  padding-bottom: 12px;
`;
const CellWrapper = styled.td`
  &.question {
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }
`;

export function SeriesTableRow({ data, tool, isAgentResult = false, onAgentCellComplete }) {
  const { prompt, cells } = data;

  const dispatch = useDispatch();

  const favoriteQuestionData = useFavoritePrompt(prompt);
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);
  const [isDeleted, setIsDeleted] = React.useState(false);

  const [queueGetCitations, setQueueGetCitations] = React.useState(false);
  const [shouldGetCitations, setShouldGetCitations] = React.useState(false);

  const promptCheck =
    favoriteQuestionData === null ? true : !favoriteQuestionData.is_default && favoriteQuestionData.is_my_prompt;
  const showFavButton = !prompt.block_editting && promptCheck;
  const isFavorite = favoriteQuestionData?.id > 0;
  const displayPrompt = prompt.block_editting ? prompt.display_label : prompt.display_label || prompt.prompt;

  React.useEffect(() => {
    if (queueGetCitations && !shouldGetCitations) {
      setShouldGetCitations(true);
    }
  }, [queueGetCitations, shouldGetCitations]);

  const handleGetCitations = () => {
    setQueueGetCitations(true);
  };
  const handleCollapse = () => {
    setIsCollapsed(true);
  };
  const handleExpand = () => {
    setIsCollapsed(false);
  };
  const handleDeleteRow = () => {
    setIsDeleted(true);
  };
  const handleEditClick = () => {
    setIsEditing(true);
  };
  const handleFav = async () => {
    const form = {
      ...prompt,
      display_label: prompt.display_label || prompt.prompt,
      user_mode: tool,
      user_modes: [tool],
    };
    const URL = `v1/genai_favorite_prompts`;
    const { status, data } = await sendApiRequest('post', URL, form);
    if (status === 200) {
      dispatch({
        type: gridTableAnalysisActions.addFavoriteQuestion,
        payload: data,
      });
    }
  };
  const handleUnFav = async () => {
    const id = favoriteQuestionData.id;
    const URL = `v1/genai_favorite_prompts/${id}`;
    const { status, data } = await sendApiRequest('delete', URL);
    logNetRequest(status, data);
    if (status === 200) {
      dispatch({
        type: gridTableAnalysisActions.removeFavoriteQuestion,
        payload: id,
      });
    }
  };

  const defaultValues = {
    text: prompt.prompt,
    is_diff_mode: prompt.is_diff_mode,
  };

  if (isDeleted) {
    return null;
  }

  return (
    <TableRowWrapper>
      <CellWrapper className={'question'}>
        <div style={{ position: 'relative' }}>{prompt.is_diff_mode && <DiffModeFlag offset={-16} />}</div>
        {isEditing && (
          <AddPromptModal
            onDismiss={() => setIsEditing(false)}
            isEditMode
            defaultValues={defaultValues}
            favId={favoriteQuestionData?.id || null}
          />
        )}
        <CellActionsWrapper>
          {/* disable editting for now, not supported after api change*/}
          {prompt.block_editting || true ? null : (
            <Container>
              <IconButton onClick={handleEditClick}>
                <Edit size={17} />
              </IconButton>
            </Container>
          )}
          {showFavButton ? (
            <IconButton onClick={isFavorite ? handleUnFav : handleFav}>
              <Star size={20} filled={isFavorite} />
            </IconButton>
          ) : null}
          {isAgentResult || queueGetCitations ? null : (
            <IconButton onClick={handleGetCitations}>
              <CitationIcon size={17} />
            </IconButton>
          )}
          <IconButton onClick={isCollapsed ? handleExpand : handleCollapse}>
            {isCollapsed ? <Expand size={19} /> : <Collapse size={20} />}
          </IconButton>
          <Container>
            <CloseButton size={15} onClick={handleDeleteRow} />
          </Container>
        </CellActionsWrapper>
        <Body5>{displayPrompt}</Body5>
      </CellWrapper>
      {cells.map((a, i) =>
        isCollapsed ? (
          <CellWrapper key={`${i}-collapsed`}></CellWrapper>
        ) : (
          React.createElement(isAgentResult ? AgentCellAnswer : CellAnswer, {
            key: i,
            tool,
            data: a,
            showCitations: queueGetCitations,
            isLastCellInRow: i === cells.length - 1,
            onResultComplete: (result) => onAgentCellComplete && onAgentCellComplete(a, result),
          })
        )
      )}
    </TableRowWrapper>
  );
}
